import { useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  Target,
  Briefcase,
  CheckSquare,
  Megaphone,
  Settings,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  Mail,
} from 'lucide-react';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { name: string; href: string }[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Leads', href: '/leads', icon: Target },
  { name: 'Pipeline', href: '/pipeline', icon: Briefcase },
  { name: 'Contacts', href: '/contacts', icon: Users },
  { name: 'Prospects', href: '/prospects', icon: Building2 },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare },
  { name: 'Emails', href: '/emails', icon: Mail },
  {
    name: 'Marketing',
    href: '/marketing',
    icon: Megaphone,
    children: [
      { name: 'Overview', href: '/marketing' },
      { name: 'Campaigns', href: '/marketing/campaigns' },
      { name: 'Content', href: '/marketing/content' },
      { name: 'Social Posts', href: '/marketing/social' },
      { name: 'Calendar', href: '/marketing/calendar' },
    ],
  },
];

function NavLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const [expanded, setExpanded] = useState(
    item.children ? location.pathname.startsWith(item.href) : false
  );
  const isActive = location.pathname === item.href ||
    (item.children && location.pathname.startsWith(item.href + '/'));
  const Icon = item.icon;

  if (item.children) {
    return (
      <div className="animate-fade-in">
        <button
          onClick={() => setExpanded(!expanded)}
          className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
            isActive
              ? 'bg-[var(--asi-bg-tertiary)] text-[var(--asi-text)]'
              : 'text-[var(--asi-text-secondary)] hover:bg-[var(--asi-bg-secondary)] hover:text-[var(--asi-text)]'
          }`}
        >
          <div className={`p-1.5 rounded-lg transition-colors duration-200 ${
            isActive
              ? 'bg-[var(--asi-accent)] text-[var(--asi-text)]'
              : 'bg-[var(--asi-bg-tertiary)] group-hover:bg-[var(--asi-accent-light)]'
          }`}>
            <Icon className="w-4 h-4" />
          </div>
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{item.name}</span>
              <ChevronDown
                className={`w-4 h-4 text-[var(--asi-text-muted)] transition-transform duration-200 ${
                  expanded ? 'rotate-180' : ''
                }`}
              />
            </>
          )}
        </button>
        {!collapsed && expanded && (
          <div className="ml-3 mt-1 pl-6 border-l-2 border-[var(--asi-bg-tertiary)] space-y-0.5">
            {item.children.map((child, idx) => (
              <Link
                key={child.href}
                to={child.href}
                className={`block px-3 py-2 rounded-lg text-sm transition-all duration-200 animate-slide-in ${
                  location.pathname === child.href
                    ? 'bg-[var(--asi-accent-light)] text-[var(--asi-text)] font-medium'
                    : 'text-[var(--asi-text-secondary)] hover:bg-[var(--asi-bg-secondary)] hover:text-[var(--asi-text)]'
                }`}
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                {child.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 animate-fade-in ${
        isActive
          ? 'bg-[var(--asi-bg-tertiary)] text-[var(--asi-text)]'
          : 'text-[var(--asi-text-secondary)] hover:bg-[var(--asi-bg-secondary)] hover:text-[var(--asi-text)]'
      }`}
    >
      <div className={`p-1.5 rounded-lg transition-colors duration-200 ${
        isActive
          ? 'bg-[var(--asi-accent)] text-[var(--asi-text)]'
          : 'bg-[var(--asi-bg-tertiary)] group-hover:bg-[var(--asi-accent-light)]'
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      {!collapsed && <span>{item.name}</span>}
    </Link>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--asi-bg)]">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2.5 rounded-xl bg-[var(--asi-bg)] shadow-[var(--shadow-lg)] border border-[var(--asi-bg-tertiary)] text-[var(--asi-text)] transition-transform duration-200 hover:scale-105"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-[var(--asi-primary)]/30 backdrop-blur-sm z-40 animate-fade-in"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[var(--asi-bg-secondary)] border-r border-[var(--asi-bg-tertiary)] z-40 transition-all duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${sidebarOpen ? 'w-64' : 'w-[72px]'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[var(--asi-bg-tertiary)]">
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[var(--asi-primary)] flex items-center justify-center">
                <span className="text-[var(--asi-text-light)] font-bold text-sm">A</span>
              </div>
              <div>
                <span className="text-base font-semibold text-[var(--asi-text)] tracking-tight">ASI Construct</span>
                <span className="block text-[10px] text-[var(--asi-text-muted)] uppercase tracking-widest">CRM</span>
              </div>
            </div>
          ) : (
            <div className="w-8 h-8 rounded-lg bg-[var(--asi-primary)] flex items-center justify-center mx-auto">
              <span className="text-[var(--asi-text-light)] font-bold text-sm">A</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-1.5 rounded-lg hover:bg-[var(--asi-bg-tertiary)] text-[var(--asi-text-secondary)] transition-colors"
          >
            <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ height: 'calc(100% - 64px - 72px)' }}>
          {navigation.map((item, idx) => (
            <div key={item.name} style={{ animationDelay: `${idx * 50}ms` }}>
              <NavLink item={item} collapsed={!sidebarOpen} />
            </div>
          ))}
        </nav>

        {/* Settings at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[var(--asi-bg-tertiary)] bg-[var(--asi-bg-secondary)]">
          <Link
            to="/settings"
            className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[var(--asi-text-secondary)] hover:bg-[var(--asi-bg-tertiary)] hover:text-[var(--asi-text)] transition-all duration-200"
          >
            <div className="p-1.5 rounded-lg bg-[var(--asi-bg-tertiary)] group-hover:bg-[var(--asi-accent-light)] transition-colors duration-200">
              <Settings className="w-4 h-4" />
            </div>
            {sidebarOpen && <span>Settings</span>}
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-[72px]'
        }`}
      >
        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
